import { useState } from 'react';
import { Bell, X, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Property } from '../types/property';

interface AreaAlertsProps {
  defaultArea?: string;
  defaultCity?: string;
  onClose: () => void;
}

export function AreaAlerts({ defaultArea, defaultCity, onClose }: AreaAlertsProps) {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    areas: defaultArea ? [defaultArea] : [],
    city: defaultCity || '',
    priceMin: '',
    priceMax: '',
    propertyTypes: [] as Property['type'][],
    channels: {
      email: true,
      whatsapp: false,
    },
    consent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const propertyTypes: Property['type'][] = ['House', 'Apartment', 'Townhouse', 'Land', 'Commercial'];

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addArea = (area: string) => {
    if (area && !formData.areas.includes(area)) {
      setFormData(prev => ({
        ...prev,
        areas: [...prev.areas, area]
      }));
    }
  };

  const removeArea = (area: string) => {
    setFormData(prev => ({
      ...prev,
      areas: prev.areas.filter(a => a !== area)
    }));
  };

  const togglePropertyType = (type: Property['type']) => {
    setFormData(prev => ({
      ...prev,
      propertyTypes: prev.propertyTypes.includes(type)
        ? prev.propertyTypes.filter(t => t !== type)
        : [...prev.propertyTypes, type]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email && !formData.phone) {
      toast.error('Please provide either an email or phone number');
      return;
    }

    if (formData.areas.length === 0) {
      toast.error('Please select at least one area');
      return;
    }

    if (!formData.consent) {
      toast.error('Please agree to receive updates');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // In a real app, this would call the API
      const alertData = {
        ...formData,
        timestamp: new Date().toISOString(),
        ip: '127.0.0.1', // Would be actual IP
      };

      console.log('Area alert subscription:', alertData);

      setIsSuccess(true);
      toast.success('Area alerts set up successfully!');

      // Track event
      console.log('Area alert subscribed', {
        areas: formData.areas,
        channels: formData.channels,
        propertyTypes: formData.propertyTypes
      });

    } catch (error) {
      toast.error('Failed to set up alerts. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="font-semibold mb-2">You're all set!</h3>
          <p className="text-muted-foreground text-sm mb-4">
            You'll receive notifications when new properties are listed in your selected areas.
          </p>
          <Button onClick={onClose} className="w-full" data-testid="area-alerts-success-close-button">
            Close
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Area Alerts
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="area-alerts-close-button">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Contact Information */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Contact Information</h4>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                placeholder="your@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => updateFormData('phone', e.target.value)}
                placeholder="+27 82 123 4567"
              />
            </div>
          </div>

          {/* Areas */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Areas of Interest</h4>
            
            <div className="flex gap-2">
              <Select value="" onValueChange={addArea}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Add an area" />
                </SelectTrigger>
                <SelectContent>
                  {[]
                    .filter(suburb => !formData.areas.includes(suburb))
                    .map((suburb) => (
                      <SelectItem key={suburb} value={suburb}>{suburb}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {formData.areas.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.areas.map((area) => (
                  <Badge key={area} variant="secondary" className="flex items-center gap-1">
                    {area}
                    <button
                      type="button"
                      onClick={() => removeArea(area)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Price Range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="priceMin">Min Price</Label>
              <Input
                id="priceMin"
                type="number"
                value={formData.priceMin}
                onChange={(e) => updateFormData('priceMin', e.target.value)}
                placeholder="500000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priceMax">Max Price</Label>
              <Input
                id="priceMax"
                type="number"
                value={formData.priceMax}
                onChange={(e) => updateFormData('priceMax', e.target.value)}
                placeholder="2000000"
              />
            </div>
          </div>

          {/* Property Types */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Property Types</h4>
            <div className="flex flex-wrap gap-2">
              {propertyTypes.map((type) => (
                <Badge
                  key={type}
                  variant={formData.propertyTypes.includes(type) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => togglePropertyType(type)}
                >
                  {type}
                </Badge>
              ))}
            </div>
          </div>

          {/* Notification Channels */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm">How would you like to be notified?</h4>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="email-notifications"
                  checked={formData.channels.email}
                  onCheckedChange={(checked) => 
                    updateFormData('channels', { ...formData.channels, email: checked })
                  }
                />
                <Label htmlFor="email-notifications" className="text-sm">
                  Email notifications
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="whatsapp-notifications"
                  checked={formData.channels.whatsapp}
                  onCheckedChange={(checked) => 
                    updateFormData('channels', { ...formData.channels, whatsapp: checked })
                  }
                />
                <Label htmlFor="whatsapp-notifications" className="text-sm">
                  WhatsApp updates (requires phone number)
                </Label>
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="space-y-3">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="consent"
                checked={formData.consent}
                onCheckedChange={(checked) => updateFormData('consent', checked)}
              />
              <Label htmlFor="consent" className="text-sm leading-relaxed">
                I agree to receive updates for new listings in my selected areas. 
                I understand I can unsubscribe anytime. This complies with POPIA requirements.
              </Label>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
            data-testid="area-alerts-submit-button"
          >
            {isSubmitting ? 'Setting up alerts...' : 'Get Area Alerts'}
          </Button>
        </form>

        <div className="mt-4 text-xs text-muted-foreground">
          <strong>Privacy:</strong> Your information is used only for property alerts and is never shared with third parties. 
          You can unsubscribe anytime using the link in our emails.
        </div>
      </CardContent>
    </Card>
  );
}